import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { createClient, predicate } from 'services/prismic'

import { isPresent } from 'utils'

import prismicTrunkShowToTrunkShowCardProps from 'converters/prismicTrunkShowToTrunkShowCardProps'

import Button from 'components/Button'
import Divider from 'components/Divider'
import PrismicPage from 'components/PrismicPage'
import RadioInputs from 'components/RadioInputs'
import SmallScreenModal from 'components/SmallScreenModal'
import Spacer from 'components/Spacer'
import SubHeader from 'components/SubHeader'
import Text from 'components/Text'
import TrunkShowCard from 'components/TrunkShowCard'

import { Column, Columns, Filter, Main, Placeholder, TrunkShows } from './index.style'

const categoryOptions = [
  { label: 'All', value: null },
  { label: 'Clothing', value: 'Clothing' },
  { label: 'Accessories', value: 'Accessories' },
  { label: 'Footwear', value: 'Footwear' },
]

const locationOptions = [
  { label: 'Asia', value: 'Asia' },
  { label: 'United States', value: 'United States' },
  { label: 'World Wide', value: null },
]

export default function Index({ pageDocument, trunkShows }) {
  const router = useRouter()

  const [categoryOption, setCategoryOption] = useState(router.query.category || null)
  const [locationOption, setLocationOption] = useState(router.query.location || null)

  const handleCategoryChange = (value) => setCategoryOption(value)
  const handleLocationChange = (value) => setLocationOption(value)

  const reset = () => {
    setCategoryOption(null)
    setLocationOption(null)
  }

  useEffect(() => {
    const query = {}

    if (isPresent(categoryOption)) query.category = categoryOption
    if (isPresent(locationOption)) query.location = locationOption

    router.push({
      pathname: '/custom-bespoke/trunk-shows',
      query,
    })
  }, [categoryOption, locationOption, router])

  return (
    <>
      <Spacer y="insetY" />
      {pageDocument && <PrismicPage document={pageDocument} />}
      <Spacer y="insetY" />
      <Main>
        <Filter>
          <SmallScreenModal toggleText="Filter">
            <SubHeader>Filter</SubHeader>
            <Spacer y={2} />
            <Text weight="bold">Category</Text>
            <Spacer y={1} />
            <RadioInputs options={categoryOptions} value={categoryOption} onChange={handleCategoryChange} />
            <Spacer y={4} />
            <Text weight="bold">Location</Text>
            <Spacer y={1} />
            <RadioInputs options={locationOptions} value={locationOption} onChange={handleLocationChange} />
          </SmallScreenModal>
        </Filter>
        <TrunkShows>
          {isPresent(trunkShows) ? (
            <Columns>
              {trunkShows.map(prismicTrunkShowToTrunkShowCardProps).map((props, index) => (
                <Column key={`trunk-show-${props.id || index}`}>
                  <TrunkShowCard {...props} />
                </Column>
              ))}
            </Columns>
          ) : (
            <>
              <Placeholder>
                <Text>No matching trunk shows found</Text>
              </Placeholder>
              <Spacer y={8} />
              <Button text="View all" onClick={reset} />
            </>
          )}
        </TrunkShows>
      </Main>
      <Spacer y={16} />
      <Divider />
    </>
  )
}

const locationTagMap = {
  Asia: ['HK', 'SH'],
  'United States': ['NY'],
  WorldWide: null,
}

export async function getServerSideProps({ previewData, query }) {
  // Fix location tag handling for WorldWide or no selection
  const locationTags = query.location ? locationTagMap[query.location] || [] : null
  const customOfferingType = query.category

  const prismic = createClient({ previewData })
  const pageDocument = await prismic.getPage('trunk-shows')

  // Calculate today's date for date filtering - use beginning of day
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0) // Use UTC to avoid timezone issues
  const todayStr = today.toISOString().split('T')[0]

  // Show trunk shows that end today or in the future
  const predicates = [predicate.dateAfter('my.trunk_show.end_date', todayStr)]

  if (locationTags && locationTags.length > 0) {
    predicates.unshift(predicate.any('my.trunk_show.location_tag', locationTags))
  }

  let trunkShows = await prismic.getAllByType('trunk_show', {
    predicates: predicates,
    fetch: [
      'trunk_show.title',
      'trunk_show.location',
      'trunk_show.location_tag',
      'trunk_show.location_group',
      'trunk_show.start_date',
      'trunk_show.end_date',
      'trunk_show.cover_image',
      'trunk_show.artisan',
      'trunk_show.summary',
    ],
    fetchLinks: ['artisan.name', 'artisan.custom_offering_types'],
    orderings: {
      field: 'my.trunk_show.start_date',
      direction: 'asc',
    },
  })

  if (customOfferingType) {
    trunkShows = trunkShows.filter((trunkShow) => {
      const customOfferingTypes = trunkShow.data.artisan.data.custom_offering_types.map(
        ({ custom_offering_type }) => custom_offering_type
      )
      return customOfferingTypes.includes(customOfferingType)
    })
  }

  return {
    props: {
      pageDocument,
      trunkShows,
    },
  }
}
